import React from 'react'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { classToFComponent, FCClassContext, LifesHubColors } from 'src/utils/views'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

import { DataGrid, GridColDef } from '@mui/x-data-grid'

import StyledDataGrid from '../../the-consumer-market/views/helpers/tcm_datagrid'
import { formatNumber } from '../../the-consumer-market/views/helpers/tcm_view_utils'
import { LinkedInPageScope } from '../mv_scopes'
import type { ContatoRow } from '../mv_types'
import { LoadError, renderLink } from './mv_components'
import { formatCnpj } from './mv_tables'

const CustomDataGrid = StyledDataGrid as typeof DataGrid

const useStyles = makeStyles()({
    page: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0
    },
    table: {
        height: 'calc(100vh - 300px)',
        minHeight: 480
    }
})

const GridBox = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    gap: 10,
    '& .MuiDataGrid-root': { flexGrow: 1, minHeight: 0 }
})

const Resumo = styled('div')({
    color: LifesHubColors.muted,
    fontSize: 12.5
})

const COLUMNS: GridColDef[] = [
    { field: 'cnpj', headerName: 'CNPJ', width: 150, valueFormatter: (value) => formatCnpj(value) },
    { field: 'nome', headerName: 'Nome', width: 230 },
    { field: 'pessoa', headerName: 'Pessoa', width: 200 },
    { field: 'cargo', headerName: 'Cargo', width: 200 },
    { field: 'titulo', headerName: 'Título', minWidth: 300, flex: 1 },
    {
        field: 'score',
        headerName: 'Score',
        width: 80,
        type: 'number',
        valueFormatter: (value) => (value === null || value === undefined ? '' : String(value).replace('.', ','))
    },
    {
        field: 'perfilUrl',
        headerName: 'Perfil',
        width: 110,
        sortable: false,
        renderCell: ({ value }) => renderLink(value, 'LinkedIn')
    },
    { field: 'telefone', headerName: 'Telefone', width: 140, sortable: false }
]

const rowId = (row: ContatoRow) => `${row.cnpj}|${row.perfilUrl}`

type LinkedInPageViewProps = {
    className?: string
    scope: LinkedInPageScope
}

class LinkedInPageViewClass implements FCClassContext<LinkedInPageViewProps> {
    scope!: LinkedInPageScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState({ scope }: LinkedInPageViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    render({ className }: LinkedInPageViewProps) {
        const { classes, scope } = this
        const termo = scope.filtro.trim().toLowerCase()
        const rows = termo
            ? scope.rows.filter((r) =>
                  `${r.cnpj} ${r.nome} ${r.pessoa} ${r.cargo} ${r.titulo}`.toLowerCase().includes(termo)
              )
            : scope.rows

        return (
            <div className={className}>
                <div className={classes.page}>
                    <LoadError message={scope.erro} />
                    <TcmCard
                        className={classes.table}
                        caption="Contatos LinkedIn"
                        count={
                            scope.loaded
                                ? `${formatNumber(scope.total)} contatos de ${formatNumber(
                                      scope.empresasComContato
                                  )} empresas`
                                : undefined
                        }
                    >
                        <GridBox>
                            {termo && <Resumo>{formatNumber(rows.length)} contatos encontrados</Resumo>}
                            <CustomDataGrid
                                rows={rows}
                                columns={COLUMNS}
                                getRowId={rowId}
                                density="compact"
                                loading={scope.loading}
                                initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                                pageSizeOptions={[25, 50, 100]}
                                disableRowSelectionOnClick
                                disableColumnSelector
                            />
                        </GridBox>
                    </TcmCard>
                </div>
            </div>
        )
    }
}

export const LinkedInPageView = classToFComponent(LinkedInPageViewClass, React)
